import { useEffect } from "preact/hooks";
import type { JSX } from "preact";
import HandIcon from "../assets/icon-hand.svg";

export type TooltipAnchor =
  | "center"
  | "top-center"
  | "bottom-center"
  | "bottom-left"
  | "right-center"
  | { top?: string | number; left?: string | number; right?: string | number; bottom?: string | number; transform?: string };

interface OnboardingTooltipProps {
  visible: boolean;
  text: string;
  anchor: TooltipAnchor;
  /**
   * Which edge of the tooltip sprouts the notch. Defaults to no notch.
   * For a tooltip sitting above its target, use `bottom`; the notch then
   * points down toward the control below.
   */
  arrow?: "top" | "bottom" | "left" | "right" | "none";
  /**
   * Offset along the notch edge. For `top`/`bottom`, this is the horizontal
   * position of the notch ('50%' = centred); for `left`/`right`, vertical.
   * Lets a wide tooltip's notch target a specific control beneath it rather
   * than the centre of the tooltip body.
   */
  arrowOffset?: string;
  /** Optional className escape hatch (e.g. for z-index overrides). */
  className?: string;
  /**
   * If set, the tooltip auto-advances after this many ms of being visible.
   * The timer resets whenever `visible` transitions to true, so gated tooltips
   * (those whose `visible` depends on scene state) only count down while shown.
   */
  autoAdvanceMs?: number;
  /** Called when the auto-advance timer fires. */
  onAutoAdvance?: () => void;
}

// Popover surface family (menu sheet / QR popover, node 2541:1317): dark plum
// glass + the design's signature white-80 hairline. White text and the white
// hand glyph — same language as every other pill on the canvas.
const TOOLTIP_BG = "rgba(27, 7, 58, 0.9)";
// The notch can't be translucent (it overlaps the pill and would double-
// darken), so it uses the same pre-composited plum the QR popover notch does.
const NOTCH_BG = "rgb(43, 28, 72)";
const HAIRLINE = "1px solid rgba(255, 255, 255, 0.8)";

const wrapperBase: JSX.CSSProperties = {
  position: "absolute",
  pointerEvents: "none",
  zIndex: 80,
  // Defeat abspos shrink-to-fit (same trick as .ms-hint): a wrapper at
  // left: 50% would otherwise cap the pill at half the viewport and force
  // a premature wrap. The pill's own maxWidth still bounds long copy.
  width: "max-content",
};

function resolveAnchor(anchor: TooltipAnchor): JSX.CSSProperties {
  if (typeof anchor === "object") return anchor;
  switch (anchor) {
    case "center":
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    case "top-center":
      return { top: "12%", left: "50%", transform: "translateX(-50%)" };
    case "bottom-center":
      return {
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
      };
    case "bottom-left":
      return {
        left: "calc(1.1rem + 54px)",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.4rem)",
      };
    case "right-center":
      return { top: "50%", right: "calc(12px + 110px)", transform: "translateY(-50%)" };
  }
}

const pillStyle: JSX.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  textAlign: "center",
  gap: 10,
  padding: "12px 20px",
  borderRadius: 100,
  background: TOOLTIP_BG,
  border: HAIRLINE,
  color: "var(--brand-text)",
  fontSize: "clamp(14px, 1.4vw, 16px)",
  fontWeight: 500,
  lineHeight: 1.3,
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  boxShadow: "0 10px 32px rgba(0, 0, 0, 0.35)",
  maxWidth: "calc(78vw - 2.5rem)",
};

/**
 * Notch — a rotated square poking out of the pill edge, carrying the same
 * hairline on its two exposed sides (the ms-qr-popover__notch pattern), so
 * the outline reads as one continuous shape instead of a flat triangle.
 */
function notchStyle(side: "top" | "bottom" | "left" | "right", offset: string): JSX.CSSProperties {
  const base: JSX.CSSProperties = {
    position: "absolute",
    width: 12,
    height: 12,
    background: NOTCH_BG,
  };
  switch (side) {
    case "top":
      return { ...base, top: -6, left: offset, transform: "translateX(-50%) rotate(45deg)", borderLeft: HAIRLINE, borderTop: HAIRLINE };
    case "bottom":
      return { ...base, bottom: -6, left: offset, transform: "translateX(-50%) rotate(45deg)", borderRight: HAIRLINE, borderBottom: HAIRLINE };
    case "left":
      return { ...base, left: -6, top: offset, transform: "translateY(-50%) rotate(45deg)", borderLeft: HAIRLINE, borderBottom: HAIRLINE };
    case "right":
      return { ...base, right: -6, top: offset, transform: "translateY(-50%) rotate(45deg)", borderTop: HAIRLINE, borderRight: HAIRLINE };
  }
}

export function OnboardingTooltip({ visible, text, anchor, arrow = "none", arrowOffset = "50%", className, autoAdvanceMs, onAutoAdvance }: OnboardingTooltipProps) {
  useEffect(() => {
    if (!visible || !autoAdvanceMs) return;
    const cb = onAutoAdvance;
    const id = setTimeout(() => cb?.(), autoAdvanceMs);
    return () => clearTimeout(id);
    // onAutoAdvance intentionally omitted — captured at timer creation so the
    // timer doesn't restart when the parent re-renders with a new arrow fn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, autoAdvanceMs]);

  if (!visible) return null;

  const positionStyle = resolveAnchor(anchor);

  return (
    <div
      className={className}
      style={{
        ...wrapperBase,
        ...positionStyle,
      }}
      role="status"
      aria-live="polite"
    >
      {/* Mount animation lives on the inner element so it can't fight the
          anchor's centering transform on the wrapper. */}
      <div className="tooltip-enter" style={{ position: "relative" }}>
        {/* Notch first (under the pill) so the pill's translucent ground
            paints over the overlap — only the protruding half shows. */}
        {arrow !== "none" && <span aria-hidden="true" style={notchStyle(arrow, arrowOffset)} />}
        <div style={pillStyle}>
          {text}
          <img src={HandIcon} alt="" className="tooltip-hand" style={{ width: 26, height: 21, flex: "0 0 auto" }} />
        </div>
      </div>
    </div>
  );
}
